import { useQuery } from '@tanstack/react-query';
import type {
  AdminAdminThroughput as AdminThroughput,
  AdminFunnelWeek as FunnelWeek,
  AdminPromiseStats as PromiseStats,
  AdminRepeatSubmitters as RepeatSubmitters,
  AdminSdkVersionCount as SdkVersionCount,
  AdminStatusFlowWeek as StatusFlowWeek,
  AdminSubmissionsWeek as SubmissionsWeek,
  AdminTenantStats as TenantStats,
  AdminWebhookStats as WebhookStats,
} from '../generated/api/types.gen.ts';
import { statsGet } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { AdminThroughput, FunnelWeek, PromiseStats, RepeatSubmitters, SdkVersionCount, StatusFlowWeek, SubmissionsWeek, TenantStats, WebhookStats };

/** DD-16: ingen andel under 30 i nevneren. Panelet regner — modellen har bare tellinger. */
export const PROSENT_GULV = 30;

/** Nøklene eies her og bare her (`react-ts-integration`). */
export const statsNoekkel = {
  alle: ['stats'] as const,
  for: (weeks: number, productId: string | null) => ['stats', weeks, productId ?? ''] as const,
};

/**
 * `GET /v1/admin/stats` — leietakerens tall for de siste ukene (M4.2, maaling.md).
 * Tallene bor der de hører hjemme: statusflyt og gjentakende innsendere på Saker, tragt og
 * versjoner ved produktene, sløyfen ut ved webhooks, tid til første status på innboksens topp.
 */
export function useStats(weeks = 12, productId: string | null = null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: statsNoekkel.for(weeks, productId),
    staleTime: 60_000,
    queryFn: async (): Promise<TenantStats> => {
      const res = await statsGet({ client: klient, query: { weeks, ...(productId ? { productId } : {}) } });
      // Et svar uten flow-liste er en feil, ikke nulltall — skjermen viser feiltilstanden.
      if (!res.response?.ok || !Array.isArray((res.data as { flow?: unknown } | undefined)?.flow)) throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
      return res.data as TenantStats;
    },
  });
}

/** Andel som tekst, eller null under gulvet — «Ikke nok data ennå» er den som viser (DD-16). */
export function andel(teller: number, nevner: number): string | null {
  if (nevner < PROSENT_GULV) return null;
  return `${Math.round((teller / nevner) * 100)} %`;
}
